import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Search, LogOut, Package, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useOrders } from '../context/OrderContext';

function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [scrolled, setScrolled] = useState(false);
    const { user, logout } = useAuth();
    const { cart, removeItem, clearCart, getCartTotal, itemCount } = useCart();
    const { orders } = useOrders();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => { setIsMenuOpen(false); }, [location]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
            setSearchQuery('');
        }
    };

    const handleLogout = () => { logout(); navigate('/'); };

    const navItems = [
        { path: '/',        label: 'Home' },
        { path: '/products', label: 'Prodotti' },
        { path: '/services', label: 'Servizi' },
        { path: '/salon',    label: 'Il Salone' },
        { path: '/staff',    label: 'Staff' },
        { path: '/gallery',  label: 'Galleria' },
        { path: '/contact',  label: 'Contatti' },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <>
            <header className={`bg-white sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-lg py-2' : 'shadow-sm py-4'}`}>
                <div className="container mx-auto px-6">
                    <div className="flex items-center justify-between">

                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-3 group">
                            <div className="gradient-pink w-11 h-11 rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                                <span className="text-white font-heading font-bold text-xl italic">M</span>
                            </div>
                            <div>
                                <h1 className="font-heading text-xl font-bold italic text-primary-pink leading-tight">
                                    Madame Afro Mayor
                                </h1>
                                <p className="text-xs font-semibold text-dark-brown tracking-widest uppercase">
                                    Salone specializzato
                                </p>
                            </div>
                        </Link>

                        {/* Ricerca desktop */}
                        <form onSubmit={handleSearch} className="hidden md:flex items-center flex-1 max-w-sm mx-8">
                            <div className="relative w-full">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Cerca prodotti o servizi..."
                                    className="w-full px-4 py-2 pl-10 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-pink/30 focus:border-primary-pink text-sm"
                                />
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            </div>
                        </form>

                        {/* Azioni desktop */}
                        <div className="hidden md:flex items-center gap-4">
                            {user ? (
                                <div className="relative group">
                                    <button className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-light-pink transition-colors">
                                        <div className="w-8 h-8 gradient-pink rounded-full flex items-center justify-center">
                                            <User size={16} className="text-white" />
                                        </div>
                                        <span className="font-medium text-sm text-gray-700">{user.name?.split(' ')[0]}</span>
                                    </button>

                                    <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                        <div className="px-4 py-3 border-b border-gray-100">
                                            <p className="font-semibold text-gray-900 text-sm">{user.name}</p>
                                            <p className="text-xs text-gray-400 mt-0.5">{user.email}</p>
                                        </div>
                                        <Link to="/orders" className="flex items-center gap-3 px-4 py-2.5 hover:bg-light-pink text-sm text-gray-700 transition-colors">
                                            <Package size={15} className="text-primary-pink" />
                                            I miei ordini ({orders?.length || 0})
                                        </Link>
                                        <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 hover:bg-light-pink text-sm text-gray-700 transition-colors">
                                            <User size={15} className="text-primary-pink" />
                                            Il mio profilo
                                        </Link>
                                        {user.role === 'admin' && (
                                            <Link to="/admin" className="flex items-center gap-3 px-4 py-2.5 hover:bg-light-pink text-sm font-semibold text-primary-pink transition-colors">
                                                <LayoutDashboard size={15} />
                                                Pannello Admin
                                            </Link>
                                        )}
                                        <div className="border-t border-gray-100 mt-1">
                                            <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-2.5 hover:bg-red-50 text-sm text-red-500 transition-colors">
                                                <LogOut size={15} />
                                                Esci
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-primary-pink transition-colors px-3 py-2">
                                        Accedi
                                    </Link>
                                    <Link to="/register" className="gradient-pink text-white px-5 py-2 rounded-full text-sm font-semibold hover:shadow-md transition-shadow">
                                        Registrati
                                    </Link>
                                </div>
                            )}

                            {/* Carrello */}
                            <button
                                onClick={() => setIsCartOpen(true)}
                                className="relative p-2.5 rounded-full hover:bg-light-pink transition-colors"
                            >
                                <ShoppingCart size={20} className="text-gray-700" />
                                {itemCount > 0 && (
                                    <span className="absolute -top-1 -right-1 gradient-pink text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                        {itemCount > 9 ? '9+' : itemCount}
                                    </span>
                                )}
                            </button>

                            <Link to="/booking" className="gradient-pink text-white px-5 py-2 rounded-full font-semibold text-sm hover:shadow-md transition-shadow">
                                Prenota
                            </Link>
                        </div>

                        {/* Hamburger */}
                        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-light-pink transition-colors">
                            {isMenuOpen ? <X size={24} className="text-primary-pink" /> : <Menu size={24} className="text-gray-700" />}
                        </button>
                    </div>

                    {/* Nav desktop */}
                    <nav className="hidden md:flex justify-center gap-1 mt-3">
                        {navItems.map(item => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`relative px-4 py-2 text-sm font-medium rounded-full transition-all ${
                                    isActive(item.path)
                                        ? 'text-primary-pink bg-light-pink'
                                        : 'text-gray-600 hover:text-primary-pink hover:bg-light-pink'
                                }`}
                            >
                                {item.label}
                                {isActive(item.path) && (
                                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-pink" />
                                )}
                            </Link>
                        ))}
                    </nav>
                </div>

                {/* Menu mobile */}
                {isMenuOpen && (
                    <div className="md:hidden border-t border-gray-100 bg-white">
                        {/* Ricerca */}
                        <form onSubmit={handleSearch} className="px-6 py-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Cerca..."
                                    className="w-full px-4 py-2.5 pl-10 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-pink/30 text-sm"
                                />
                                <Search className="absolute left-3 top-3 text-gray-400" size={16} />
                            </div>
                        </form>

                        {/* Link */}
                        <div className="px-4 pb-4 space-y-1">
                            {navItems.map(item => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                                        isActive(item.path)
                                            ? 'bg-light-pink text-primary-pink'
                                            : 'text-gray-600 hover:bg-light-pink hover:text-primary-pink'
                                    }`}
                                >
                                    {item.label}
                                </Link>
                            ))}

                            <div className="border-t border-gray-100 pt-3 mt-2 space-y-1">
                                {user ? (
                                    <>
                                        <div className="px-4 py-2">
                                            <p className="font-semibold text-gray-900 text-sm">{user.name}</p>
                                            <p className="text-xs text-gray-400">{user.email}</p>
                                        </div>
                                        <Link to="/orders" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-light-pink">
                                            <Package size={16} className="text-primary-pink" /> I miei ordini
                                        </Link>
                                        <Link to="/profile" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-light-pink">
                                            <User size={16} className="text-primary-pink" /> Il mio profilo
                                        </Link>
                                        {user.role === 'admin' && (
                                            <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-primary-pink hover:bg-light-pink">
                                                <LayoutDashboard size={16} /> Pannello Admin
                                            </Link>
                                        )}
                                        <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm text-red-500 hover:bg-red-50">
                                            <LogOut size={16} /> Esci
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link to="/login" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-light-pink">
                                            <User size={16} className="text-primary-pink" /> Accedi
                                        </Link>
                                        <Link to="/register" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-light-pink">
                                            Registrati
                                        </Link>
                                    </>
                                )}
                                <button onClick={() => setIsCartOpen(true)} className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-light-pink">
                                    <div className="flex items-center gap-3">
                                        <ShoppingCart size={16} className="text-primary-pink" />
                                        Carrello
                                    </div>
                                    {itemCount > 0 && <span className="gradient-pink text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{itemCount}</span>}
                                </button>
                                <Link to="/booking" className="block w-full text-center gradient-pink text-white px-6 py-3 rounded-full font-semibold text-sm mt-2">
                                    Prenota appuntamento
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* Drawer carrello */}
            {isCartOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
                    <div className="bg-white w-full max-w-sm h-full shadow-2xl flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h2 className="font-heading text-xl font-bold text-dark-brown">Il mio Carrello</h2>
                            <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-light-pink rounded-full transition-colors">
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        {cart.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center p-6">
                                <ShoppingCart size={56} className="text-gray-200 mb-4" />
                                <p className="text-gray-500 mb-4">Il carrello è vuoto</p>
                                <Link to="/products" onClick={() => setIsCartOpen(false)} className="text-primary-pink font-medium hover:underline text-sm">
                                    Vai allo shopping →
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                    {cart.map(item => (
                                        <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                            {item.image && <img src={item.image} alt={item.name} className="w-14 h-14 rounded-lg object-cover" />}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm text-gray-900 truncate">{item.name}</p>
                                                <p className="text-xs text-gray-500">{item.quantity} × €{item.price.toFixed(2)}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-sm text-primary-pink">€{(item.quantity * item.price).toFixed(2)}</p>
                                                <button onClick={() => removeItem(item.id)} className="text-xs text-red-400 hover:text-red-600 mt-1">Rimuovi</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="p-6 border-t space-y-3">
                                    <div className="flex justify-between font-bold text-lg">
                                        <span className="text-dark-brown">Totale</span>
                                        <span className="gradient-text-pink">{(getCartTotal ? getCartTotal() : 0).toFixed(2)} €</span>
                                    </div>
                                    <button onClick={() => { navigate('/checkout'); setIsCartOpen(false); }}
                                        className="w-full gradient-pink text-white py-3 rounded-full font-semibold hover:shadow-md transition-shadow">
                                        Procedi al Checkout
                                    </button>
                                    <button onClick={clearCart} className="w-full border border-gray-200 text-gray-500 py-2.5 rounded-full text-sm hover:bg-gray-50 transition-colors">
                                        Svuota carrello
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default Header;
